import type { JSON_ExtendedDuration } from '@src/types';

export type ExtendedDurationUnits = 'ms' | 'Y' | 'M' | 'D' | 'h' | 'm' | 's';

export function getExtendedDurationComponents(duration: JSON_ExtendedDuration): Array<[ExtendedDurationUnits, number]> {
  const regex = /(-?\d+)(ms|Y|M|D|h|m|s)/g;
  const result: Array<[ExtendedDurationUnits, number]> = [];

  let match;
  while ((match = regex.exec(duration)) !== null) {
    const value = parseInt(match[1], 10);
    const unit = match[2];

    result.push([unit as ExtendedDurationUnits, value]);
  }

  return result;
}

export function prettyExtendedDuration(duration: JSON_ExtendedDuration): string {
  const parts: string[] = [];

  // Map unit abbreviations to full names
  const unitNames: Record<string, string> = {
    Y: 'year',
    M: 'month',
    D: 'day',
    h: 'hour',
    m: 'minute',
    s: 'second',
    ms: 'millisecond',
  };

  for (const [unit, value] of getExtendedDurationComponents(duration)) {
    if (!value) {
      continue; // skip zeros
    }
    const base = unitNames[unit];
    const absValue = Math.abs(value);
    const label = absValue === 1 ? base : `${base}s`;
    parts.push(`${value} ${label}`);
  }

  return parts.join(', ');
}

const unitToMs: Record<ExtendedDurationUnits, number> = {
  ms: 1,
  s: 1000,
  m: 60 * 1000,
  h: 60 * 60 * 1000,
  D: 24 * 60 * 60 * 1000,
  M: 30 * 24 * 60 * 60 * 1000, // treating month as 30 days
  Y: 365 * 24 * 60 * 60 * 1000, // treating year as 365 days
};

/**
 * Gets an approximation of the number of milliseconds an extended duration represents
 */
export function extendedDurationToMs(duration: JSON_ExtendedDuration): number {
  let total = 0;

  for (const [unit, value] of getExtendedDurationComponents(duration)) {
    total += value * unitToMs[unit];
  }

  return total;
}

export function extendedDurationComparator(a: JSON_ExtendedDuration, b: JSON_ExtendedDuration): number {
  const msA = extendedDurationToMs(a);
  const msB = extendedDurationToMs(b);

  if (msA > msB) {
    return 1;
  } else if (msA < msB) {
    return -1;
  }
  return 0;
}
